import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteFeedPost,
  fetchFeedEligibility,
  fetchFeedPosts,
  markFeedSeen,
  voteFeedPost,
} from "../../api";
import CommentThread from "./CommentThread";
import FeedLockedCard from "./FeedLockedCard";
import FeedProfile from "./FeedProfile";
import FeedReportModal from "./FeedReportModal";
import GenderRequiredCard from "./GenderRequiredCard";
import MajlisHeader from "./MajlisHeader";
import MajlisSidebar, { BlockedUsersCard } from "./MajlisSidebar";
import PostCard from "./PostCard";

/**
 * MajlisPage — the social feed at /majlis, styled after
 * design/majlis-final-reference.html: sticky المجلس header, 1060px
 * two-column RTL grid (feed right, 312px sidebar left, hidden <920px).
 *
 * Same API/state logic as the old FeedView modal — relocated, not rewritten.
 * The feed is gender-segregated server-side; browser back (or the brand
 * link) is the exit.
 *
 * Legacy sub-components (composer, locked/gender cards, profile) still speak
 * the app's dark-scope token set, so they render inside local .dark-scope
 * wrappers rather than dark-scoping the whole page.
 */
export default function MajlisPage({ currentUser, isAdmin = false, onOpenTyping, onOpenAdmin, onSeen }) {
  const [eligibility, setEligibility] = useState(null);
  const [tab, setTab] = useState("global"); // global | following
  const [view, setView] = useState("feed"); // feed | profile
  const [profileUserId, setProfileUserId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [showHarakat, setShowHarakat] = useState(true);
  const [openThreadPostId, setOpenThreadPostId] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the header search → one request per typing pause.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Visiting the page IS the "seen" event — covers deep links and reloads.
  useEffect(() => {
    markFeedSeen().catch(() => {});
    onSeen && onSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEligibility = useCallback(() => {
    fetchFeedEligibility()
      .then(setEligibility)
      .catch(() => {});
  }, []);

  const loadFeed = useCallback((activeTab) => {
    return fetchFeedPosts({ tab: activeTab, q: debouncedSearch }).catch((e) => {
      if (e.status === 403) return { results: [], next_cursor: null };
      throw e;
    });
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchFeedEligibility(), loadFeed(tab)])
      .then(([elig, page]) => {
        if (cancelled) return;
        setEligibility(elig);
        setPosts(page.results || []);
        setNextCursor(page.next_cursor);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, loadFeed]);

  const reloadFeed = useCallback(() => {
    loadFeed(tab)
      .then((page) => {
        setPosts(page.results || []);
        setNextCursor(page.next_cursor);
      })
      .catch(() => {});
  }, [tab, loadFeed]);

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetchFeedPosts({ tab, cursor: nextCursor, q: debouncedSearch })
      .then((page) => {
        setPosts((prev) => [...prev, ...(page.results || [])]);
        setNextCursor(page.next_cursor);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleDelete = async (post) => {
    if (!window.confirm("حذف هذا المنشور؟")) return;
    try {
      await deleteFeedPost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch {
      /* best-effort; the next reload reconciles */
    }
  };

  // Declarative optimistic vote: clicking the active direction clears it.
  const handleVote = async (post, direction) => {
    const desired = post.my_vote === direction ? 0 : direction;
    const apply = (p, my_vote, like_count, down_count) =>
      p.id === post.id
        ? { ...p, my_vote, like_count, down_count, score: like_count - down_count }
        : p;
    const optimisticUp = post.like_count + (desired === 1 ? 1 : 0) - (post.my_vote === 1 ? 1 : 0);
    const optimisticDown = post.down_count + (desired === -1 ? 1 : 0) - (post.my_vote === -1 ? 1 : 0);
    setPosts((prev) => prev.map((p) => apply(p, desired, optimisticUp, optimisticDown)));
    try {
      const res = await voteFeedPost(post.id, desired);
      setPosts((prev) => prev.map((p) => apply(p, res.my_vote, res.like_count, res.down_count)));
    } catch {
      setPosts((prev) => prev.map((p) => apply(p, post.my_vote, post.like_count, post.down_count)));
    }
  };

  const handleCommentCount = (postId, delta) => {
    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, comment_count: p.comment_count + delta } : p))
    );
  };

  const openProfile = (author) => {
    if (!author?.id) return;
    setProfileUserId(author.id);
    setOpenThreadPostId(null);
    setView("profile");
  };

  const navigate = useNavigate();

  const canRead = eligibility?.can_read;
  const noGender = eligibility && !canRead && eligibility.reason === "no_gender";
  const underAge = eligibility && !canRead && eligibility.reason === "under_min_age";
  const feedOff = eligibility && !canRead && eligibility.reason === "feed_disabled";
  // no_handle still shows the composer — it renders the one-time handle step.
  const canPost = eligibility?.can_post || eligibility?.reason === "no_handle";
  const typingLocked = eligibility && !canPost && eligibility.reason === "typing_incomplete";
  const suspended = eligibility && !canPost && eligibility.reason === "suspended";

  const renderThread = (post) => (
    <CommentThread
      post={post}
      canPost={!!eligibility?.can_post}
      currentUserId={currentUser?.id}
      isAdmin={isAdmin}
      showHarakat={showHarakat}
      onCountChange={handleCommentCount}
    />
  );

  const centeredNote = (text, tone = "var(--mj-muted)") => (
    <p className="font-plex text-sm text-center py-8" style={{ color: tone }}>
      {text}
    </p>
  );

  return (
    <div dir="rtl" lang="ar" className="majlis-scope min-h-screen font-plex">
      <MajlisHeader
        currentUser={{ ...(currentUser || {}), ...(eligibility || {}) }}
        isAdmin={isAdmin}
        showHarakat={showHarakat}
        onToggleHarakat={() => setShowHarakat((v) => !v)}
        onCreate={canPost ? () => navigate("/majlis/submit") : null}
        onOpenAdmin={onOpenAdmin}
        onSearch={setSearch}
        searchValue={search}
      />

      {/* .shell — main (right in RTL) + 312px aside (left), sidebar gone <920px */}
      <div className="max-w-[1060px] mx-auto px-4 pt-5 pb-20 grid grid-cols-1 min-[920px]:grid-cols-[minmax(0,1fr)_312px] gap-6">
        <main className="min-w-0">
          {loading && centeredNote("جارٍ التحميل…")}
          {error && !loading && centeredNote(error, "var(--mj-flag)")}

          {!loading && noGender && (
            <div className="dark-scope">
              <GenderRequiredCard onSet={() => { loadEligibility(); reloadFeed(); }} />
            </div>
          )}
          {!loading && underAge && centeredNote("المجلس متاح لمن بلغ الثامنة عشرة.")}
          {!loading && feedOff && centeredNote("المجلس غير متاح الآن.")}

          {!loading && canRead && view === "profile" && (
            <div>
              <FeedProfile
                userId={profileUserId}
                onBack={() => { setView("feed"); reloadFeed(); }}
                showHarakat={showHarakat}
                onVote={handleVote}
                onOpenComments={(post) =>
                  setOpenThreadPostId((cur) => (cur === post.id ? null : post.id))
                }
                commentsOpenFor={openThreadPostId}
                renderThread={renderThread}
                isAdmin={isAdmin}
              />
            </div>
          )}

          {!loading && canRead && view === "feed" && (
            <>
              {/* sortbar — the two real tabs as the mockup's pills */}
              <div className="flex items-center gap-1.5 mb-3.5">
                {[["global", "✧ الأحدث"], ["following", "المتابَعون"]].map(([key, label]) => {
                  const on = tab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setTab(key); setOpenThreadPostId(null); }}
                      className="text-[13.5px] rounded-full px-3.5 py-1.5"
                      style={
                        on
                          ? {
                              background: "var(--mj-gold-soft)",
                              color: "var(--mj-gold)",
                              fontWeight: 700,
                              border: "1px solid var(--mj-gold-line)",
                            }
                          : { color: "var(--mj-muted)" }
                      }
                      onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "var(--mj-raised)"; }}
                      onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Create-post prompt bar (Reddit-style): a fake-input that
                  routes to the full /majlis/submit editor — the live fuṣḥā
                  checker lives there, not in an inline quick composer. */}
              {canPost && (
                <button
                  onClick={() => navigate("/majlis/submit")}
                  className="w-full flex items-center gap-3 rounded-[14px] px-4 py-3 mb-3 text-right"
                  style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--mj-ink-6)")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--mj-line)")}
                >
                  <span
                    className="flex-1 rounded-xl px-4 py-2.5 font-arabic text-[17px]"
                    style={{
                      background: "var(--mj-raised)",
                      border: "1px solid var(--mj-line)",
                      color: "var(--mj-muted)",
                    }}
                  >
                    اُكتُبْ بِالفُصْحَى…
                  </span>
                  <span
                    className="rounded-full px-4 py-1.5 text-[12.5px] font-bold shrink-0"
                    style={{ background: "var(--mj-gold)", color: "var(--mj-bg)" }}
                  >
                    أنشئ منشورًا
                  </span>
                </button>
              )}
              {typingLocked && (
                <div className="dark-scope mb-3">
                  <FeedLockedCard typingGate={eligibility.typing_gate} onOpenTyping={onOpenTyping} />
                </div>
              )}
              {suspended && (
                <div
                  className="rounded-[14px] px-4 py-3 mb-3 text-sm"
                  style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)", color: "var(--mj-cream)" }}
                >
                  النشرُ موقوفٌ في حسابك
                  {eligibility.suspended_until
                    ? ` حتى ${new Date(eligibility.suspended_until).toLocaleDateString("ar")}`
                    : ""}
                  . لا يزال بإمكانك القراءة والتصويت.
                </div>
              )}

              {posts.length === 0 &&
                centeredNote(
                  debouncedSearch
                    ? `لا نتائجَ عن «${debouncedSearch}»`
                    : tab === "following"
                    ? "تابِعْ أحدًا لترى منشوراته هنا."
                    : "لا شيءَ هنا بعد — كُنْ أوّلَ من يكتبُ بالفصحى."
                )}
              <ul className="list-none p-0 m-0">
                {posts.map((post) => (
                  <li key={post.id} className="mb-3">
                    <PostCard
                      post={post}
                      showHarakat={showHarakat}
                      isAdmin={isAdmin}
                      onDelete={handleDelete}
                      onVote={handleVote}
                      onOpenComments={(p) =>
                        setOpenThreadPostId((cur) => (cur === p.id ? null : p.id))
                      }
                      onOpenProfile={openProfile}
                      onReport={(p) => setReportTarget(p)}
                      commentsOpen={openThreadPostId === post.id}
                    />
                    {openThreadPostId === post.id && renderThread(post)}
                  </li>
                ))}
              </ul>
              {nextCursor && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full rounded-full px-3 py-2 text-[13.5px] font-semibold disabled:opacity-50"
                  style={{
                    background: "var(--mj-raised)",
                    color: "var(--mj-muted)",
                    border: "1px solid var(--mj-line)",
                  }}
                >
                  {loadingMore ? "جارٍ التحميل…" : "المزيد"}
                </button>
              )}

              {/* phones: the sidebar is hidden, so the unblock route lives
                  at the bottom of the feed instead */}
              <div className="min-[920px]:hidden mt-4">
                <BlockedUsersCard onOpenProfile={openProfile} refreshKey={view} />
              </div>
            </>
          )}
        </main>

        <MajlisSidebar onOpenProfile={openProfile} refreshKey={view} />
      </div>

      {reportTarget && (
        <FeedReportModal postId={reportTarget.id} onClose={() => setReportTarget(null)} />
      )}
    </div>
  );
}
